import * as applicationService from '../../services/ApplicationService';
import * as subscriptionService from '../../services/SubscriptionService';
import * as userService from '../../services/UserService';
import { normalizeJson } from '../../../shared/helpers';
import { defineStore } from "pinia";
import { useRepo } from "pinia-orm";
import { useAuthenticationStore } from './authentication';
import Application from '../../models/Application';
import Subscription from '../../models/Subscription';

function initialState() {
    return {
        checkedAllApplications: false,
        checkedApplications: [],
        filterValues: {},
        sequence: [],
    };
}

export const useApplicationsStore = defineStore('applicationsModule', {
    state: () => ({
        ...initialState(),
        applicationRepo: useRepo(Application),
    }),
    getters: {
        applications(state) {
            const sort = state.filterValues.sort || '-created_at';
            let field;
            let direction;

            if (sort.charAt(0) == '-') {
                field = sort.substr(1);
                direction = 'desc';
            } else {
                field = sort;
                direction = 'asc';
            }

            if (['first_name', 'last_name'].includes(field)) {
                const attr = field;
                field = (application) => application[attr].toLowerCase();
            }

            if (['score'].includes(field)) {
                const attr = field;
                field = (application) => application[attr] ? application[attr] : -1;
            }

            return this.applicationRepo
                .with('stage', (query) => {
                    query.with('job');
                })
                .with('subscriptions')
                .with('interim')
                .orderBy(field, direction)
                .get();
        },
        getURLParametersForFilter(state) {
            const params = {},
                score = [state.filterValues.score_min, state.filterValues.score_max].join(',');

            if (state.filterValues.date_from) {
                params['filter[date_from]'] = state.filterValues.date_from?.format();
            }

            if (state.filterValues.date_to) {
                params['filter[date_to]'] = state.filterValues.date_to?.format();
            }

            if (state.filterValues.rejected && state.filterValues.rejected.length) {
                params['filter[rejected]'] = state.filterValues.rejected.join(',');
            }

            if (state.filterValues.hired) {
                params['filter[hired]'] = state.filterValues.hired;
            }

            if (state.filterValues.new) {
                params['filter[new]'] = state.filterValues.new;
            }

            if (state.filterValues.stages && state.filterValues.stages.length) {
                params['filter[stage_id]'] = state.filterValues.stages.map(stage => {
                    const parts = stage.split(':');
                    return parts[parts.length - 1];
                }).join(',');
            }

            if (state.filterValues.score) {
                if (state.filterValues.unrated !== 0) {
                    params['filter[score_between_or_unrated]'] = score;
                } else {
                    params['filter[score_between]'] = score;
                }
            } else {
                if (state.filterValues.unrated !== -1) {
                    params['filter[unrated]'] = state.filterValues.unrated;
                }
            }

            if (state.filterValues.text_keyword !== '') {
                params['filter[keyword]'] = state.filterValues.text_keyword;
            }

            if (state.filterValues.jobfunctions && state.filterValues.jobfunctions.length) {
                params['filter[job_id]'] = state.filterValues.jobfunctions.join(',');
            }

            if (state.filterValues.tags && state.filterValues.tags.length) {
                params['filter[tag_id]'] = state.filterValues.tags.join(',');
                params['tag_mode'] = state.filterValues.inclusiveTagMode ? 'and' : 'or';
            }

            if (state.filterValues.offices && state.filterValues.offices.length) {
                params['filter[interim_id]'] = state.filterValues.offices.join(',');
            }

            if (state.filterValues.referrals && state.filterValues.referrals.length) {
                params['filter[referrer_source]'] = state.filterValues.referrals.join(',');
            }

            if (state.filterValues.favorites) {
                params['filter[favorites]'] = state.filterValues.favorites;
            }

            if (state.filterValues.stale) {
                params['filter[stale]'] = state.filterValues.stale;
            }

            if (state.filterValues.interview) {
                params['filter[interview]'] = state.filterValues.interview;
            }

            if (state.filterValues.unconfirmedInterview) {
                params['filter[unconfirmedInterviews]'] = state.filterValues.interview;
            }

            if (state.filterValues.user) {
                params['filter[user_id]'] = state.filterValues.user;
            }

            if (state.filterValues.job) {
                params['filter[job_id]'] = state.filterValues.job.id;
            }

            return params;
        },
    },
    actions: {
        find(id) {
            return applicationService.find(id)
                .then(result => {
                    return this.applicationRepo.save(normalizeJson(result));
                });
        },
        fetchApplicationSubscriptions(application) {
            subscriptionService.getApplicationSubscriptions(application.id)
                .then(result => {
                    return useRepo(Subscription).save(normalizeJson(result.data));
                });
        },
        setFavorite({ applicationId, favorite }) {
            this.applicationRepo
                .where('id', applicationId)
                .update({ favorite: !favorite });

            userService
                .updateFavorite(useAuthenticationStore().currentUserId, parseInt(applicationId))
                .catch(() => {
                    this.applicationRepo
                        .where('id', applicationId)
                        .update({ favorite: !favorite });
                });
        },
        resetState() {
            // Instead of resetting the state entirely, we want to keep track
            // of the filter values which were set before.
            // If we switch pages or change order sequence, we want to use
            // the same filter values.
            const newState = initialState();
            delete newState.filterValues;
            delete newState.sequence;

            Object.assign(this, newState);

            // delete all applications so we will reload them
            this.clearApplications();
        },
        clearApplications() {
            this.applicationRepo.flush();
        },
        checkAllApplications(applications) {
            this.checkedApplications = applications;
            this.checkedAllApplications = true;
        },
        checkApplication({ applicationId, isChecked }) {
            const items = [];

            for (const x of this.checkedApplications) {
                if (x.id !== applicationId) {
                    items.push(x);
                }
            }

            if (isChecked) {
                items.push(
                    this.applicationRepo
                        .with('stage')
                        .find(applicationId),
                );
            }

            this.checkedApplications = items;
            this.checkedAllApplications = false;
        },
        updateApplications({ applications }) {
            this.checkedApplications = [...applications];
            this.checkedAllApplications = false;
        },
        clearCheckedApplications() {
            this.checkedAllApplications = false;
            this.checkedApplications = [];
        },
        setFilterValues(values) {
            this.filterValues = values;
        },
        setSequence(ids) {
            this.sequence = ids;
        },
    },
});
